import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { CallInformation } from './call-information.model';
import { SKIP_HTTP_ERROR_LOG_FOR_404 } from '../../core/interceptors/http-error.interceptor';
import { environment } from '../../../environments/environment';

/** Outcome of {@link CallLookupService.findCallByCchsid}. Never throws — always resolves. */
export type CallLookupResult =
  | { status: 'found'; call: CallInformation }
  | { status: 'not-found' }
  | { status: 'error' };

/** Not-found copy — shared by every cchsid lookup surface (unified search, Call Viewer deep link). */
export const CALL_NOT_AVAILABLE_TEXT = 'This CCHSID is not available.';
/** Error headline — shared by every cchsid lookup surface (unified search, Call Viewer deep link). */
export const CALL_LOAD_ERROR_TEXT = 'The call could not be loaded.';

/**
 * Looks up a single call by its cchsid via the phoneservice single-call endpoint.
 * Stateless — results are consumed directly by the caller.
 *
 * Endpoint: GET /phoneservice/v3/calls/callinformation/{cchsid} (EB-4154).
 * A 404 is an expected, routine outcome (unknown cchsid) — it is not logged at all via
 * {@link SKIP_HTTP_ERROR_LOG_FOR_404} rather than treated as an operational fault.
 * @ticket EB-4153
 */
@Injectable({ providedIn: 'root' })
export class CallLookupService {
  private readonly http = inject(HttpClient);

  /**
   * Load one call by its cchsid.
   * @param cchsid sent verbatim (callers trim only, never change case); URL-encoded as a path segment.
   * @returns a single-emission Observable that never errors: `found` with the call, `not-found`
   *   on 404 (not logged), `error` on any other failure (logged by the HTTP error interceptor).
   * @ticket EB-4153
   */
  findCallByCchsid(cchsid: string): Observable<CallLookupResult> {
    return this.http
      .get<CallInformation>(
        `${environment.phoneServiceUrl}/calls/callinformation/${encodeURIComponent(cchsid)}`,
        { context: new HttpContext().set(SKIP_HTTP_ERROR_LOG_FOR_404, true) },
      )
      .pipe(
        map((call): CallLookupResult => ({ status: 'found', call })),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return of<CallLookupResult>({ status: 'not-found' });
          }
          return of<CallLookupResult>({ status: 'error' });
        }),
      );
  }
}
